import {
    ExperiencePurchaseItem,
    Form,
    GiftPurchaseItem,
    LineItem,
    LineItemCode,
    LineItemObject,
    LineItemType,
    MembershipPurchaseItem,
    MerchandisePurchaseItem,
    PartnerObject,
    PurchaseItem,
    PurchaseItemObject,
} from "types";
import { shouldSkipQuestionnaireModal } from "./Field";

export const canCancel = (purchaseItem: PurchaseItem | ExperiencePurchaseItem) => {
    if (
        purchaseItem.object === PurchaseItemObject.GIFT ||
        purchaseItem.object === PurchaseItemObject.STORE_CREDIT ||
        purchaseItem.object === PurchaseItemObject.MEMBERSHIP
    ) {
        return false;
    }

    const cancellableStatuses = [
        PurchaseItemStatus.CONFIRMED,
        PurchaseItemStatus.PENDING_ACCEPT,
        PurchaseItemStatus.PENDING_MINIMUM,
    ];

    return cancellableStatuses.includes(purchaseItem.status);
};

export enum PurchaseItemStatus {
    DRAFT = "draft",
    HOLD = "hold", // 103
    PENDING_ACCEPT = "pending_accept", // 100
    PENDING_MINIMUM = "pending_minimum", // 101
    CONFIRMED = "confirmed", // 200
    DECLINED = "declined", // 300
    CANCELED = "canceled", // 700
    ABANDONED = "abandoned",
    WAITLIST_CREATED = "waitlist_created",
    WAITLIST_CONFIRMED = "waitlist_confirmed",
    WAITLIST_REMOVED = "waitlist_removed",
}

export const PurchaseItemStatusCopy: { [key in PurchaseItemStatus]: string } = {
    [PurchaseItemStatus.DRAFT]: "Draft",
    [PurchaseItemStatus.HOLD]: "Hold",
    [PurchaseItemStatus.PENDING_ACCEPT]: "Pending Accept",
    [PurchaseItemStatus.PENDING_MINIMUM]: "Pending Minimum",
    [PurchaseItemStatus.CONFIRMED]: "Confirmed",
    [PurchaseItemStatus.DECLINED]: "Declined",
    [PurchaseItemStatus.CANCELED]: "Canceled",
    [PurchaseItemStatus.ABANDONED]: "Abandoned",
    [PurchaseItemStatus.WAITLIST_CREATED]: "Waitlist Created",
    [PurchaseItemStatus.WAITLIST_CONFIRMED]: "Waitlist Confirmed",
    [PurchaseItemStatus.WAITLIST_REMOVED]: "Waitlist Removed",
};

export const isPurchaseItemCanceled = (item: PurchaseItem) => {
    return [PurchaseItemStatus.CANCELED, PurchaseItemStatus.DECLINED].includes(item.status);
};

export const isExperiencePurchaseItem = (item: PurchaseItem): item is ExperiencePurchaseItem => {
    return (
        item.object === PurchaseItemObject.EXPERIENCE ||
        item.object === PurchaseItemObject.ORDER ||
        item.object === PurchaseItemObject.EQUIPMENT
    );
};

export const isStoreCreditPurchaseItem = (item?: PurchaseItem) => item?.object === PurchaseItemObject.STORE_CREDIT;

export const getItemLineItemsByType = (item: PurchaseItem, type: LineItemType) => {
    return item.lineItems?.filter((lineItem) => type === lineItem.type);
};

export const getItemLineItemsByTypes = (item: PurchaseItem, types: LineItemType[]) => {
    return item.lineItems?.filter((lineItem) => types.includes(lineItem.type));
};

export const getLineItemsByCode = (item: PurchaseItem, code: LineItemCode) => {
    return item.lineItems?.filter((lineItem) => code === lineItem.code);
};

export const getLineItemByCode = (item: PurchaseItem, code: LineItemCode) => {
    return item.lineItems?.find((lineItem) => code === lineItem.code);
};

export const getSortedLineItems = (item: PurchaseItem): LineItem[] => {
    const lineItemTypeOrder: LineItemType[] = [
        LineItemType.DEMOGRAPHIC,
        LineItemType.EQUIPMENT,
        LineItemType.MEMBERSHIP,
        LineItemType.ADDON,
        LineItemType.PICKUP,
        LineItemType.WARE,
        LineItemType.GRATUITY,
        LineItemType.GIFT,
        LineItemType.ADJUSTMENT,
        LineItemType.TAXED_ADJUSTMENT,
        LineItemType.DISCOUNT,
        LineItemType.PACKAGE_DISCOUNT,
        LineItemType.COUPON_DISCOUNT,
        LineItemType.AFFILIATE_DISCOUNT,
        LineItemType.FEEABLE_TAXABLE_ADJUSTMENT,
        LineItemType.FEE,
        LineItemType.TAX,
        LineItemType.LEGACY_PLUGIN_FEE,
        LineItemType.VOID_DISCOUNT,
        LineItemType.TYPE_ADHOC_ADJUSTMENT,
    ];

    const lineItems = [];
    for (const lineItemType of lineItemTypeOrder) {
        const lineItemsOfType = getItemLineItemsByType(item, lineItemType);
        if (lineItemsOfType) {
            lineItems.push(...lineItemsOfType);
        }
    }

    return lineItems;
};

export const getScheduledExperiencePurchaseItem = (items: PurchaseItem[]): ExperiencePurchaseItem | undefined => {
    return items.find((item) => item.object === PurchaseItemObject.EXPERIENCE) as ExperiencePurchaseItem;
};

export const hasStatus = (status: PurchaseItemStatus, statuses: PurchaseItemStatus[]) => {
    return statuses.includes(status);
};

export const isGiftPurchaseItem = (item?: PurchaseItem): item is GiftPurchaseItem => {
    return item?.object === PurchaseItemObject.GIFT;
};

// Recipient details are optional on gifts, so a gift can have neither a name nor an email.
export const hasGiftRecipient = (item?: PurchaseItem): item is GiftPurchaseItem => {
    return isGiftPurchaseItem(item) && [item.recipientName, item.recipientEmail].some(Boolean);
};

export const isNonCanceledExperienceItem = (item: ExperiencePurchaseItem) =>
    isExperiencePurchaseItem(item) && !isCanceled(item);

export const isOrganizerOnlyForm = (item: PurchaseItem) => {
    return (item.form?.organizerOnly || item.formResponse) && !item.guests?.at(0)?.formResponse;
};

export const isAdditionalInformationQuestionsExistInForm = (form?: Form) => {
    return form?.fields?.some((field) => shouldSkipQuestionnaireModal(field)) ?? false;
};

export const isCanceled = (item?: PurchaseItem) => {
    return item?.status === PurchaseItemStatus.CANCELED;
};

export const getMembershipDiscountLineItem = (item?: PurchaseItem) => {
    return item?.lineItems?.find((lineItem: LineItem) => isMembershipDiscountLineItem(lineItem));
};

export const getGiftApplicablePurchaseItem = (
    items: PurchaseItem[],
): ExperiencePurchaseItem | MembershipPurchaseItem | undefined => {
    const itemTypes = [PurchaseItemObject.EXPERIENCE, PurchaseItemObject.ORDER, PurchaseItemObject.MEMBERSHIP];

    return itemTypes.map((type) => items.find((item) => item.object === type)).find((item) => item !== undefined) as
        | ExperiencePurchaseItem
        | MembershipPurchaseItem
        | undefined;
};

export const isMerchandiseItem = (item?: PurchaseItem | null): item is MerchandisePurchaseItem => {
    return item?.object === PurchaseItemObject.WARE;
};

export const isMembershipItem = (item?: PurchaseItem | null): item is MembershipPurchaseItem => {
    return item?.object === PurchaseItemObject.MEMBERSHIP;
};

export const isMembershipDiscountLineItem = (lineItem: LineItem) => {
    return (
        lineItem.object === LineItemObject.DISCOUNT_CODE && lineItem.templateCode === LineItemCode.MEMBERSHIP_DISCOUNT
    );
};

export const hasAffiliate = (item: ExperiencePurchaseItem) => {
    return !!item.partner?.id && item.partner?.object === PartnerObject.AFFILIATE;
};

export const isEquipmentItem = (item?: PurchaseItem | null): boolean => {
    return item?.object === PurchaseItemObject.EQUIPMENT;
};
